import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CatalogService, InventoryLot, InventorySerial, InventoryTraceabilityNode } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-traceability',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, ButtonModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.tracking.traceability.title')" [description]="t('catalog.tracking.traceability.description')">
      <ain-inventory-page-hint pageKey="traceability" />
      <section class="search-panel">
        <label>
          <span>{{ t('catalog.tracking.lotId') }}</span>
          <p-select [options]="lotOptions()" [(ngModel)]="query.lotId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.tracking.serialNumber') }}</span>
          <p-select [options]="serialOptions()" [(ngModel)]="query.serialId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
        </label>
        <button pButton type="button" [label]="t('catalog.action.search')" (click)="search()"></button>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (searched() && nodes().length === 0) {
        <ain-state type="empty" [title]="t('catalog.tracking.traceability.empty')" />
      } @else if (nodes().length > 0) {
        <section class="tree">
          @for (node of nodes(); track node.id) {
            <article class="node">
              <header>
                <strong>{{ node.nodeType }}</strong>
                <span>{{ node.label }}</span>
                <em>{{ node.status }}</em>
              </header>
              @if (node.children.length > 0) {
                <div class="children">
                  @for (child of node.children; track child.id) {
                    <div class="child">
                      <strong>{{ child.nodeType }}</strong> {{ child.label }} ({{ child.status }})
                      @if (child.children.length > 0) {
                        <ul>
                          @for (grand of child.children; track grand.id) {
                            <li>{{ grand.nodeType }}: {{ grand.label }} — {{ grand.status }}</li>
                          }
                        </ul>
                      }
                    </div>
                  }
                </div>
              }
            </article>
          }
        </section>
      }

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .search-panel { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); align-items: end; margin-bottom: 12px; }
    .search-panel label { display: grid; gap: 6px; }
    .search-panel span { font-size: 12px; color: #475569; font-weight: 600; }
    .tree { display: grid; gap: 10px; }
    .node { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
    .node header { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .node em { color: #64748b; font-style: normal; }
    .children { margin-top: 10px; padding-left: 12px; border-left: 2px solid #e2e8f0; display: grid; gap: 8px; }
    .child ul { margin: 6px 0 0; padding-left: 18px; color: #475569; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryTraceabilityComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(false);
  readonly searched = signal(false);
  readonly error = signal('');
  readonly nodes = signal<InventoryTraceabilityNode[]>([]);
  readonly lots = signal<InventoryLot[]>([]);
  readonly serials = signal<InventorySerial[]>([]);
  readonly query = { lotId: '', serialId: '' };

  readonly lotOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.lots().map(l => ({ label: l.lotNumber, value: l.id }))
  ]);
  readonly serialOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.serials().map(s => ({ label: s.serialNumber, value: s.id }))
  ]);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => {
        this.loadSelectors();
        if (this.searched()) this.search();
      }
    );
    this.loadSelectors();
  }

  private loadSelectors(): void {
    this.service.listInventoryLots().subscribe({ next: rows => this.lots.set(rows) });
    this.service.listInventorySerials().subscribe({ next: rows => this.serials.set(rows) });
  }

  search(): void {
    const lotNumber = this.lots().find(l => l.id === this.query.lotId)?.lotNumber;
    const serialNumber = this.serials().find(s => s.id === this.query.serialId)?.serialNumber;
    if (!lotNumber && !serialNumber) {
      this.error.set(this.t('catalog.tracking.traceability.queryRequired'));
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.searched.set(true);
    this.service.getInventoryTraceability({ lotNumber, serialNumber }).subscribe({
      next: result => {
        this.nodes.set(result.nodes);
        this.loading.set(false);
      },
      error: () => {
        this.nodes.set([]);
        this.error.set(this.t('catalog.tracking.traceability.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
